import { useEffect, useMemo, useRef, useState } from 'react'

import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import GetAppIcon from '@material-ui/icons/GetApp'
import StarBorderIcon from '@material-ui/icons/StarBorder'
import StarIcon from '@material-ui/icons/Star'
import { makeStyles } from '@material-ui/core/styles'
import { toast } from 'react-toastify'

import { getToolkitExportLabel } from '../../consts/toolkit-export-actions'
import { FlowAction } from '../../core/types/chat.types'
import { MaxChatMessage } from '../../types'
import PdfFileIcon from '../icons/pdf-file-icon'
import WordFileIcon from '../icons/word-file-icon'
import PowerPointFileIcon from '../icons/powerpoint-file-icon'

type MessageToolkitProps = {
  message: MaxChatMessage
  isFavorited: boolean
  showExport: boolean
  exportAction?: FlowAction
  exportActions?: FlowAction[]
  visible?: boolean
  disableAsync?: boolean
  onFavorite: (message: MaxChatMessage) => void
  onShare?: (message: MaxChatMessage) => void
  onRetry?: (message: MaxChatMessage) => void
  onExportBudget: (payload: { message: MaxChatMessage; action: FlowAction }) => void
  onExportReport?: (payload: { message: MaxChatMessage; action: FlowAction }) => void
}

const MIN_LOCK_MS = 800

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: 'var(--ds-fg-tertiary, #8c92a5)',
  },
  button: {
    height: 28,
    minWidth: 28,
    padding: '0 4px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'inherit',
    cursor: 'pointer',
    transition: 'all 160ms ease',
    '&:hover': {
      borderColor: 'var(--ds-border-default, #d7dae4)',
      background: 'var(--ds-bg-subtle, #f1f2f7)',
      color: 'var(--ds-fg-primary, #1f2430)',
    },
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  icon: {
    fontSize: 16,
  },
  fileIcon: {
    height: 20,
    width: 'auto',
  },
}))

const getExportFileIcon = (actionValue: string, iconClass: string) => {
  if (actionValue === 'EXP_PDF_MSG') return <PdfFileIcon className={iconClass} />
  if (actionValue === 'EXP_WORD_MSG') return <WordFileIcon className={iconClass} />
  if (actionValue === 'EXPORT_MSG_PPT') return <PowerPointFileIcon className={iconClass} />
  return <GetAppIcon style={{ fontSize: 16 }} />
}

const MessageToolkit = ({
  message,
  isFavorited,
  showExport,
  exportAction,
  exportActions = [],
  visible = true,
  disableAsync = false,
  onFavorite,
  onExportBudget,
  onExportReport,
}: MessageToolkitProps) => {
  const classes = useStyles()
  const [locked, setLocked] = useState(false)
  const lockTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (lockTimeoutRef.current !== null) {
        window.clearTimeout(lockTimeoutRef.current)
        lockTimeoutRef.current = null
      }
    }
  }, [])

  if (!visible) return null

  const lockExport = async (work: () => Promise<void> | void) => {
    const startedAt = Date.now()
    setLocked(true)

    try {
      await work()
    } finally {
      const release = () => {
        setLocked(false)
        if (lockTimeoutRef.current !== null) {
          window.clearTimeout(lockTimeoutRef.current)
          lockTimeoutRef.current = null
        }
      }

      const remainingMs = MIN_LOCK_MS - (Date.now() - startedAt)
      if (remainingMs > 0) {
        lockTimeoutRef.current = window.setTimeout(release, remainingMs)
      } else {
        release()
      }
    }
  }

  const isExportDisabled = disableAsync || locked

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content || '')
      toast.success('Copied!')
    } catch {
      toast.error('Something went wrong')
    }
  }

  const handleExportBudget = async () => {
    if (!exportAction || isExportDisabled) return
    await lockExport(async () => {
      onExportBudget({ message, action: exportAction })
    })
  }

  const handleExportReport = async (action: FlowAction) => {
    if (isExportDisabled || !onExportReport) return
    await lockExport(async () => {
      onExportReport({ message, action })
    })
  }

  return (
    <div className={classes.root}>
      {/* Copy */}
      <button
        type="button"
        className={classes.button}
        title="Copy"
        aria-label="Copy"
        onClick={() => { void handleCopy() }}
      >
        <FileCopyOutlinedIcon className={classes.icon} />
      </button>

      {/* Favorite */}
      <button
        type="button"
        className={classes.button}
        title={isFavorited ? 'Unfavorite' : 'Favorite'}
        aria-label={isFavorited ? 'Unfavorite' : 'Favorite'}
        onClick={() => onFavorite(message)}
      >
        {isFavorited ? (
          <StarIcon className={classes.icon} />
        ) : (
          <StarBorderIcon className={classes.icon} />
        )}
      </button>

      {/* Budget export */}
      {showExport && exportAction ? (
        <button
          type="button"
          className={`${classes.button} ${isExportDisabled ? classes.disabledButton : ''}`}
          disabled={isExportDisabled}
          title="Export Budget"
          aria-label="Export Budget"
          onClick={() => { void handleExportBudget() }}
        >
          <GetAppIcon style={{ fontSize: 20 }} />
        </button>
      ) : null}

      {/* Message-level export actions (PDF / DOCX / PPT) */}
      {exportActions.map((action) => (
        <button
          key={action.actionValue}
          type="button"
          className={`${classes.button} ${isExportDisabled ? classes.disabledButton : ''}`}
          disabled={isExportDisabled}
          title={getToolkitExportLabel(action.actionValue, action.actionText)}
          aria-label={getToolkitExportLabel(action.actionValue, action.actionText)}
          onClick={() => { void handleExportReport(action) }}
        >
          {getExportFileIcon(action.actionValue, classes.fileIcon)}
        </button>
      ))}
    </div>
  )
}

export default MessageToolkit
